import ky from 'ky';
import { useAuthStore } from '../stores/auth.store';
import type { FilterConfig } from '../types/filter';

const API_BASE = '/api';

// Create base client
const api = ky.create({
  prefixUrl: API_BASE,
  timeout: 30000,
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().accessToken;
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        // Handle 401 - try to refresh token
        if (response.status === 401 && !request.url.includes('/auth/refresh')) {
          const refreshed = await useAuthStore.getState().refreshToken();
          if (refreshed) {
            // Retry original request
            const token = useAuthStore.getState().accessToken;
            request.headers.set('Authorization', `Bearer ${token}`);
            return ky(request);
          } else {
            // Logout user
            useAuthStore.getState().logout();
          }
        }
        return response;
      },
    ],
  },
});

export default api;

// Auth API
export const authApi = {
  register: (data: { email: string; password: string; displayName?: string }) =>
    api.post('auth/register', { json: data }).json<{
      user: unknown;
      accessToken: string;
      message: string;
    }>(),
    
  login: (data: { email: string; password: string }) =>
    api.post('auth/login', { json: data }).json<{
      user: unknown;
      accessToken: string;
    }>(),
    
  refresh: () =>
    api.post('auth/refresh').json<{
      user: unknown;
      accessToken: string;
    }>(),
    
  logout: () =>
    api.post('auth/logout').json<{ message: string }>(),
    
  me: () =>
    api.get('auth/me').json<{ user: unknown }>(),
    
  verifyEmail: (token: string) =>
    api.post('auth/verify-email', { json: { token } }).json<{ user: unknown; message: string }>(),
    
  forgotPassword: (email: string) =>
    api.post('auth/forgot-password', { json: { email } }).json<{ message: string }>(),
    
  resetPassword: (token: string, password: string) =>
    api.post('auth/reset-password', { json: { token, password } }).json<{ message: string }>(),
    
  deleteAccount: () =>
    api.delete('auth/account').json<{ message: string }>(),
};

// Mail API
export const mailApi = {
  getAccounts: () =>
    api.get('mail/accounts').json<{ accounts: unknown[] }>(),
    
  getFolders: (accountId: string) =>
    api.get(`mail/${accountId}/folders`).json<{ folders: unknown[] }>(),
    
  getMessages: (accountId: string, params?: {
    folderId?: string;
    labelId?: string;
    baseFolderId?: string; // Base folder for category columns (filters category within folder)
    limit?: number;
    offset?: number;
    search?: string;
  }) =>
    api.get(`mail/${accountId}/messages`, { searchParams: params as Record<string, string | number> }).json<{
      messages: unknown[];
      hasMore: boolean;
    }>(),
    
  // Smart column messages with filter
  getSmartMessages: (accountId: string, data: {
    filterConfig: FilterConfig;
    baseSourceType?: 'folder' | 'category' | 'label' | 'all';
    baseSourceId?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.post(`mail/${accountId}/messages/smart`, { json: data }).json<{
      messages: unknown[];
      hasMore: boolean;
      usedClientFilter: boolean;
    }>(),
    
  getMessage: (accountId: string, messageId: string) =>
    api.get(`mail/${accountId}/message`, { searchParams: { id: messageId } }).json<{ message: unknown }>(),
    
  // Batch get messages - uses MS Graph $batch API (20 requests per HTTP call)
  // Much more efficient for AI columns than parallel getMessage calls
  getMessagesBatch: (accountId: string, messageIds: string[]) =>
    api.post(`mail/${accountId}/messages/batch`, { 
      json: { messageIds },
      timeout: 60000, // Longer timeout for batch
    }).json<{ 
      messages: unknown[]; 
      errors: string[];
    }>(),
    
  getOpenUrl: (accountId: string, messageId: string) =>
    api.get(`mail/${accountId}/message/open-url`, { searchParams: { id: messageId } }).json<{ url: string }>(),
    
  getReplyUrl: (accountId: string, messageId: string) =>
    api.get(`mail/${accountId}/message/reply-url`, { searchParams: { id: messageId } }).json<{ url: string }>(),
    
  disconnectAccount: (accountId: string) =>
    api.delete(`mail/accounts/${accountId}`).json<{ message: string }>(),
    
  moveMessage: (accountId: string, messageId: string, targetFolderId: string) =>
    api.post(`mail/${accountId}/message/move`, { json: { messageId, targetFolderId } }).json<{ message: string }>(),
    
  setCategory: (accountId: string, messageId: string, categoryName: string) =>
    api.post(`mail/${accountId}/message/category`, { json: { messageId, categoryName } }).json<{ message: string }>(),
    
  removeCategory: (accountId: string, messageId: string, categoryName?: string) =>
    api.delete(`mail/${accountId}/message/category`, { json: { messageId, categoryName } }).json<{ message: string }>(),
    
  deleteMessage: (accountId: string, messageId: string) =>
    api.post(`mail/${accountId}/message/delete`, { json: { id: messageId } }).json<{ message: string; previousFolderId: string }>(),
    
  restoreMessage: (accountId: string, messageId: string, targetFolderId: string) =>
    api.post(`mail/${accountId}/message/restore`, { json: { messageId, targetFolderId } }).json<{ message: string }>(),
    
  // Task API
  getTasksForMessage: (accountId: string, messageId: string) =>
    api.get(`mail/${accountId}/message/tasks`, { searchParams: { id: messageId } }).json<{
      tasks: Array<{
        id: string;
        mailAccountId: string;
        messageId: string;
        title: string;
        completed: boolean;
        position: number;
        createdAt: string;
        updatedAt: string;
      }>;
    }>(),
    
  getTaskCounts: (accountId: string, messageIds: string[]) =>
    api.post(`mail/${accountId}/message/task-counts`, { json: { messageIds } }).json<{
      counts: Record<string, { total: number; completed: number }>;
    }>(),
    
  createTask: (accountId: string, messageId: string, title: string) =>
    api.post(`mail/${accountId}/message/tasks`, { 
      searchParams: { id: messageId },
      json: { title } 
    }).json<{
      task: {
        id: string;
        mailAccountId: string;
        messageId: string;
        title: string;
        completed: boolean;
        position: number;
        createdAt: string;
        updatedAt: string;
      };
    }>(),
    
  updateTask: (taskId: string, data: { title?: string; completed?: boolean; position?: number }) =>
    api.put(`mail/tasks/${taskId}`, { json: data }).json<{
      task: {
        id: string;
        mailAccountId: string;
        messageId: string;
        title: string;
        completed: boolean;
        position: number;
        createdAt: string;
        updatedAt: string;
      };
    }>(),
    
  deleteTask: (taskId: string) =>
    api.delete(`mail/tasks/${taskId}`).json<{ message: string }>(),
    
  reorderTasks: (accountId: string, messageId: string, taskIds: string[]) =>
    api.put(`mail/${accountId}/message/tasks/reorder`, { 
      searchParams: { id: messageId },
      json: { taskIds } 
    }).json<{
      tasks: Array<{
        id: string;
        title: string;
        completed: boolean;
        position: number;
      }>;
    }>(),
    
  // Attachments API
  getAttachments: (accountId: string, messageId: string) =>
    api.get(`mail/${accountId}/message/attachments`, { searchParams: { id: messageId } }).json<{
      attachments: Array<{
        id: string;
        name: string;
        contentType: string;
        size: number;
        isInline: boolean;
      }>;
    }>(),
    
  getAttachmentDownloadUrl: (accountId: string, messageId: string, attachmentId: string) =>
    `/api/mail/${accountId}/message/attachment/download?id=${encodeURIComponent(messageId)}&attachmentId=${encodeURIComponent(attachmentId)}`,
    
  // Summary API (AI-generated one-line summaries)
  getSummaries: (accountId: string, messageIds: string[]) =>
    api.post(`mail/${accountId}/message/summaries`, { json: { messageIds } }).json<{
      summaries: Record<string, string>;
    }>(),
    
  generateSummary: (accountId: string, data: {
    messageId: string;
    subject: string;
    bodyPreview: string;
    from: string;
  }) =>
    api.post(`mail/${accountId}/message/summarize`, { json: data }).json<{
      messageId: string;
      summary: string;
      cached: boolean;
    }>(),
    
  generateSummariesBatch: (accountId: string, messages: Array<{
    messageId: string;
    subject: string;
    bodyPreview: string;
    from: string;
  }>) =>
    api.post(`mail/${accountId}/message/summarize-batch`, { json: { messages } }).json<{
      summaries: Record<string, string>;
    }>(),
};

// Boards API
export const boardsApi = {
  getBoards: () =>
    api.get('boards').json<{ boards: unknown[] }>(),
    
  getBoard: (boardId: string) =>
    api.get(`boards/${boardId}`).json<{ board: unknown }>(),
    
  createBoard: (data: { name: string; icon?: string }) =>
    api.post('boards', { json: data }).json<{ board: unknown }>(),
    
  updateBoard: (boardId: string, data: { name?: string; icon?: string; hideDuplicates?: boolean; showCompletionPopup?: boolean }) =>
    api.put(`boards/${boardId}`, { json: data }).json<{ board: unknown }>(),
    
  deleteBoard: (boardId: string) =>
    api.delete(`boards/${boardId}`).json<{ message: string }>(),
    
  reorderBoards: (ids: string[]) =>
    api.put('boards/reorder', { json: { ids } }).json<{ message: string }>(),
    
  addColumn: (boardId: string, data: unknown) =>
    api.post(`boards/${boardId}/columns`, { json: data }).json<{ column: unknown }>(),
    
  updateColumn: (columnId: string, data: unknown) =>
    api.put(`boards/columns/${columnId}`, { json: data }).json<{ column: unknown }>(),
    
  deleteColumn: async (columnId: string) => {
    try {
      return await api.delete(`boards/columns/${columnId}`).json<{ message: string }>();
    } catch (error) {
      // Ignore 404 - column already deleted (idempotent)
      if (error instanceof Error && 'response' in error) {
        const httpError = error as { response?: { status?: number } };
        if (httpError.response?.status === 404) {
          return { message: 'Column already deleted' };
        }
      }
      throw error;
    }
  },
    
  reorderColumns: (boardId: string, ids: string[]) =>
    api.put(`boards/${boardId}/columns/reorder`, { json: { ids } }).json<{ message: string }>(),
};

// Assignments API
export const assignmentsApi = {
  create: (data: { mailAccountId: string; messageId: string; columnId: string }) =>
    api.post('assignments', { json: data }).json<{ assignment: unknown }>(),
    
  update: (assignmentId: string, data: { columnId?: string; pinned?: boolean; note?: string }) =>
    api.put(`assignments/${assignmentId}`, { json: data }).json<{ assignment: unknown }>(),
    
  delete: (assignmentId: string) =>
    api.delete(`assignments/${assignmentId}`).json<{ message: string }>(),
    
  getByColumn: (columnId: string) =>
    api.get('assignments', { searchParams: { columnId } }).json<{ assignments: unknown[] }>(),
};

// Admin API
export const adminApi = {
  // Settings
  getSettings: () =>
    api.get('admin/settings').json<{ settings: Record<string, any> }>(),
    
  updateOAuthSettings: (data: {
    microsoft_client_id?: string;
    microsoft_client_secret?: string;
    microsoft_redirect_uri?: string;
    google_client_id?: string;
    google_client_secret?: string;
    google_redirect_uri?: string;
  }) =>
    api.put('admin/settings/oauth', { json: data }).json<{ message: string }>(),
    
  updateTrialSettings: (data: {
    trial_duration_days: number;
    max_mail_accounts: number;
  }) =>
    api.put('admin/settings/trial', { json: data }).json<{ message: string }>(),
    
  getOAuthStatus: () =>
    api.get('admin/settings/oauth-status').json<{
      microsoft: boolean;
      google: boolean;
      anyConfigured: boolean;
    }>(),
    
  // Users
  getUsers: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('admin/users', { searchParams: params as Record<string, string | number> }).json<{
      users: Array<{
        id: string;
        email: string;
        displayName: string | null;
        role: string;
        status: string;
        emailVerified: boolean;
        trialEndsAt: string | null;
        lastLoginAt: string | null;
        createdAt: string;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(),
    
  updateUser: (userId: string, data: {
    status?: 'trial' | 'active' | 'suspended';
    trialEndsAt?: string | null;
    role?: 'user' | 'admin';
  }) =>
    api.put(`admin/users/${userId}`, { json: data }).json<{ message: string }>(),
    
  deleteUser: (userId: string) =>
    api.delete(`admin/users/${userId}`).json<{ message: string }>(),
    
  extendTrial: (userId: string, days: number) =>
    api.post(`admin/users/${userId}/extend-trial`, { json: { days } }).json<{
      message: string;
      newTrialEndsAt: string;
    }>(),
};

// Public API (no auth required)
export const publicApi = {
  getOAuthStatus: () =>
    api.get('public/settings/oauth-status').json<{
      microsoft: boolean;
      google: boolean;
      anyConfigured: boolean;
    }>(),
};

// To-Do API (Microsoft To-Do / Google Tasks)
export interface TodoList {
  id: string;
  name: string;
  isDefault?: boolean;
}

export interface TodoTask {
  id: string;
  listId: string;
  title: string;
  notes?: string;
  status: 'needsAction' | 'completed';
  dueDate?: string;
  linkedMailId?: string;
  deepLink?: string;
}

export const todoApi = {
  // Get all To-Do lists for an account
  getLists: (accountId: string) =>
    api.get(`todo/${accountId}/lists`).json<{ lists: TodoList[] }>(),
    
  // Get tasks from a specific list
  getTasks: (accountId: string, listId: string) =>
    api.get(`todo/${accountId}/lists/${listId}/tasks`).json<{ tasks: TodoTask[] }>(),
    
  // Create a new task
  createTask: (accountId: string, data: {
    listId: string;
    title: string;
    notes?: string;
    dueDate?: string;
    messageId?: string;
  }) =>
    api.post(`todo/${accountId}/tasks`, { json: data }).json<{ task: TodoTask }>(),
    
  // Update a task
  updateTask: (accountId: string, listId: string, taskId: string, data: {
    completed?: boolean;
    title?: string;
  }) =>
    api.put(`todo/${accountId}/lists/${listId}/tasks/${taskId}`, { json: data }).json<{ task: TodoTask }>(),
    
  // Quick action: Create To-Do from mail
  createFromMail: (accountId: string, data: {
    messageId: string;
    listId: string;
    title?: string;
    dueDate?: string;
  }) =>
    api.post(`todo/${accountId}/from-mail`, { json: data }).json<{ task: TodoTask; link?: MailTodoLink; message: string }>(),
  
  // Get todo link for a specific message (uses query param for long MS Graph IDs)
  getMailLink: (accountId: string, messageId: string) =>
    api.get(`todo/${accountId}/mail-link`, { searchParams: { id: messageId } }).json<{ link: MailTodoLink | null }>(),
  
  // Get todo links for multiple messages (batch)
  getMailLinksBatch: (accountId: string, messageIds: string[]) =>
    api.post(`todo/${accountId}/mail-links/batch`, { json: { messageIds } }).json<{ links: Record<string, MailTodoLink> }>(),
  
  // Toggle todo completion
  toggleMailTodo: (accountId: string, messageId: string) =>
    api.post(`todo/${accountId}/mail-link/toggle`, { json: { messageId } }).json<{ link: MailTodoLink; message: string }>(),
  
  // Remove todo link
  removeMailLink: (accountId: string, messageId: string) =>
    api.post(`todo/${accountId}/mail-link/remove`, { json: { messageId } }).json<{ message: string }>(),
  
  // Sync todo status from external providers (Microsoft/Google → Kolumi)
  syncAccount: (accountId: string) =>
    api.post(`todo/${accountId}/sync`).json<{ synced: number; updated: number; deleted: number; errors?: string[] }>(),
  
  // Sync all accounts for a user (called on app load)
  syncAll: () =>
    api.post('todo/sync-all').json<{ results: Record<string, { synced: number; updated: number; deleted: number; errors?: string[] }> }>(),
};

// Mail Todo Link type
export interface MailTodoLink {
  id: string;
  mailAccountId: string;
  messageId: string;
  externalListId: string;
  externalTaskId: string;
  title: string;
  completed: boolean;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
}

// Subtask type (checklistItems)
export interface Subtask {
  id: string;
  todoLinkId: string;
  externalId: string | null;
  displayName: string;
  isChecked: boolean;
  position: number;
  syncStatus: 'synced' | 'pending' | 'error';
  lastSyncError: string | null;
  createdAt: string;
  updatedAt: string;
}

// Subtasks API
export const subtasksApi = {
  // Get subtasks for a message's todo link
  getSubtasks: (accountId: string, messageId: string) =>
    api.get(`todo/${accountId}/subtasks`, { searchParams: { messageId } }).json<{
      todoLink: MailTodoLink | null;
      subtasks: Subtask[];
    }>(),
  
  // Sync subtasks from Microsoft
  syncSubtasks: (accountId: string, messageId: string) =>
    api.post(`todo/${accountId}/subtasks/sync`, { json: { messageId } }).json<{
      synced: number;
      added: number;
      updated: number;
      removed: number;
      subtasks: Subtask[];
      message: string;
    }>(),
  
  // Create a new subtask
  createSubtask: (accountId: string, messageId: string, displayName: string) =>
    api.post(`todo/${accountId}/subtasks`, { json: { messageId, displayName } }).json<{
      subtask: Subtask;
      message: string;
    }>(),
  
  // Update a subtask (toggle checked or rename)
  updateSubtask: (accountId: string, subtaskId: string, data: { displayName?: string; isChecked?: boolean }) =>
    api.patch(`todo/${accountId}/subtasks/${subtaskId}`, { json: data }).json<{
      subtask: Subtask;
      message: string;
    }>(),
  
  // Delete a subtask
  deleteSubtask: (accountId: string, subtaskId: string) =>
    api.delete(`todo/${accountId}/subtasks/${subtaskId}`).json<{ message: string }>(),
};

// AI API Types
export interface AiProvider {
  id: string;
  name: string;
  providerType: 'openai' | 'anthropic' | 'custom';
  baseUrl?: string;
  defaultModel: string;
  isDefault: boolean;
  settings?: {
    maxTokens?: number;
    temperature?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface SmartAiColumn {
  id: string;
  boardId: string;
  name: string;
  baseSourceType: 'folder' | 'category' | 'label' | 'all';
  baseSourceId: string;
  prompt: string;
  systemPrompt?: string;
  providerId?: string;
  position: number;
  color?: string;
  icon?: string;
  enabled: boolean;
  reEvalConfig: {
    onThreadUpdate: boolean;
    onDeadlineApproach: boolean;
    periodicDays: number | null;
    maxReEvals: number;
  };
  costConfig: {
    maxTokensPerDay: number;
    maxEvalsPerHour: number;
    skipConfidenceAbove: number;
    cacheDays: number;
  };
  createdAt: string;
  updatedAt: string;
  // Relations
  provider?: AiProvider;
}

export interface AiEvaluation {
  id: string;
  columnId: string;
  mailAccountId: string;
  messageId: string;
  matches: boolean;
  confidence: number;
  reasoning?: string;
  tokensUsed?: number;
  latencyMs?: number;
  modelUsed?: string;
  evaluatedAt: string;
}

// AI API
export const aiApi = {
  // Providers
  getProviders: () =>
    api.get('ai-providers').json<{ providers: AiProvider[] }>(),
    
  getProvider: (providerId: string) =>
    api.get(`ai-providers/${providerId}`).json<{ provider: AiProvider }>(),
    
  createProvider: (data: {
    name: string;
    providerType: 'openai' | 'anthropic' | 'custom';
    apiKey: string;
    baseUrl?: string;
    defaultModel: string;
    isDefault?: boolean;
    settings?: { maxTokens?: number; temperature?: number };
  }) =>
    api.post('ai-providers', { json: data }).json<{ provider: AiProvider }>(),
    
  updateProvider: (providerId: string, data: Partial<{
    name: string;
    apiKey: string;
    baseUrl: string | null;
    defaultModel: string;
    isDefault: boolean;
    settings: { maxTokens?: number; temperature?: number };
  }>) =>
    api.patch(`ai-providers/${providerId}`, { json: data }).json<{ provider: AiProvider }>(),
    
  deleteProvider: (providerId: string) =>
    api.delete(`ai-providers/${providerId}`),
    
  testProvider: (providerId: string) =>
    api.post(`ai-providers/${providerId}/test`).json<{ success: boolean; message?: string; model?: string }>(),
    
  getModels: (providerId: string) =>
    api.get(`ai-providers/${providerId}/models`).json<{ models: string[] }>(),
  
  // Smart AI Columns
  getAiColumns: (boardId: string) =>
    api.get(`boards/${boardId}/ai-columns`).json<{ columns: SmartAiColumn[] }>(),
    
  getAiColumn: (columnId: string) =>
    api.get(`ai-columns/${columnId}`).json<{ column: SmartAiColumn }>(),
    
  createAiColumn: (data: {
    boardId: string;
    name: string;
    baseSourceType?: 'folder' | 'category' | 'label' | 'all';
    baseSourceId?: string;
    prompt: string;
    systemPrompt?: string;
    providerId?: string;
    position?: number;
    color?: string;
    icon?: string;
    reEvalConfig?: {
      onThreadUpdate?: boolean;
      onDeadlineApproach?: boolean;
      periodicDays?: number | null;
      maxReEvals?: number;
    };
    costConfig?: {
      maxTokensPerDay?: number;
      maxEvalsPerHour?: number;
      skipConfidenceAbove?: number;
      cacheDays?: number;
    };
  }) =>
    api.post('ai-columns', { json: data }).json<{ column: SmartAiColumn }>(),
    
  updateAiColumn: (columnId: string, data: Partial<{
    name: string;
    baseSourceType: 'folder' | 'category' | 'label' | 'all';
    baseSourceId: string;
    prompt: string;
    systemPrompt: string | null;
    providerId: string | null;
    position: number;
    color: string | null;
    icon: string | null;
    enabled: boolean;
    reEvalConfig: any;
    costConfig: any;
  }>) =>
    api.patch(`ai-columns/${columnId}`, { json: data }).json<{ column: SmartAiColumn }>(),
    
  deleteAiColumn: (columnId: string) =>
    api.delete(`ai-columns/${columnId}`),
    
  getAiColumnEvaluations: (columnId: string, matchesOnly = false) =>
    api.get(`ai-columns/${columnId}/evaluations`, { 
      searchParams: matchesOnly ? { matchesOnly: 'true' } : {} 
    }).json<{ evaluations: AiEvaluation[] }>(),
    
  getAiColumnStats: (columnId: string) =>
    api.get(`ai-columns/${columnId}/stats`).json<{ stats: any }>(),
  
  // Cost/Limit Status
  getAiColumnCostStatus: (columnId: string) =>
    api.get(`ai-columns/${columnId}/cost-status`).json<{ 
      status: {
        tokensUsedToday: number;
        maxTokensPerDay: number;
        tokenLimitExceeded: boolean;
        tokenUsagePercent: number;
        evalsLastHour: number;
        maxEvalsPerHour: number;
        evalLimitExceeded: boolean;
        evalUsagePercent: number;
      };
    }>(),
    
  getBoardCostStatus: (boardId: string) =>
    api.get(`boards/${boardId}/ai-columns/cost-status`).json<{
      status: Record<string, {
        columnName: string;
        tokensUsedToday: number;
        maxTokensPerDay: number;
        tokenLimitExceeded: boolean;
        tokenUsagePercent: number;
      }>;
    }>(),
    
  boostAiColumnLimit: (columnId: string, newLimit: number) =>
    api.post(`ai-columns/${columnId}/boost-limit`, { 
      json: { newLimit } 
    }).json<{ column: SmartAiColumn; message: string }>(),
  
  // Board AI Operations
  triggerAiScan: (boardId: string, mailAccountId: string, messageIds: string[]) =>
    api.post(`boards/${boardId}/ai-scan`, { 
      json: { mailAccountId, messageIds } 
    }).json<{ message: string; queued: number }>(),
    
  getAiMatches: (boardId: string, mailAccountId: string) =>
    api.get(`boards/${boardId}/ai-matches`, { 
      searchParams: { mailAccountId } 
    }).json<{ matches: Record<string, string[]> }>(),
    
  getAiMatchesWithDetails: (boardId: string, mailAccountId: string) =>
    api.get(`boards/${boardId}/ai-matches`, { 
      searchParams: { mailAccountId, details: 'true' } 
    }).json<{ 
      matches: Record<string, Array<{ messageId: string; confidence: number; reasoning: string | null }>>;
      detailed: true;
    }>(),
  
  // Queue
  getQueueStats: () =>
    api.get('ai/queue/stats').json<{ stats: { waiting: number; active: number; completed: number; failed: number } }>(),
    
  // Get AI evaluations for a specific message (for detail view)
  getMessageEvaluations: (accountId: string, messageId: string) =>
    api.get(`mail/${accountId}/message/ai-evaluations`, {
      searchParams: { id: messageId },
    }).json<{
      evaluations: Array<{
        columnId: string;
        columnName: string;
        boardName: string;
        matches: boolean;
        confidence: number;
        reasoning: string | null;
        evaluatedAt: string;
      }>;
    }>(),
};

// User Preferences API
export interface ColumnSettings {
  summaryMode?: boolean;
}

export interface UserPreferences {
  summaryPrompt: string | null;
  defaultSummaryPrompt: string;
  columnSettings: Record<string, ColumnSettings>;
}

export const preferencesApi = {
  get: () =>
    api.get('user/preferences').json<UserPreferences>(),
    
  update: (data: { summaryPrompt?: string | null; columnSettings?: Record<string, ColumnSettings> }) =>
    api.patch('user/preferences', { json: data }).json<{ success: boolean }>(),
    
  // Convenience method to update a single column's settings
  setColumnSummaryMode: (columnId: string, summaryMode: boolean) =>
    api.patch('user/preferences', { 
      json: { columnSettings: { [columnId]: { summaryMode } } }
    }).json<{ success: boolean }>(),
};
